import * as React from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";

import type { CampaignRequestDetail, CampaignRequestEvent } from "@/hooks/api/use-campaign-requests";

import { BlockerChips, NOT_METERED } from "../components";
import {
	formatDate,
	formatDateRange,
	formatMoney,
	formatMoneyRange,
	formatPercent,
	marketFlag,
	marketLabel,
	quoteTotalRange,
} from "../constants";
import { Facts, Field, Muted, Panel, Tile } from "./parts";
import { buildTimeline } from "./request-timeline";

/**
 * What a client checks in five seconds: where it is, what we are still waiting on them for, when
 * it dials, and what it costs.
 *
 * Everything here is the headline of something the other four tabs hold in full — the two
 * timeline rows come off the same list the Activity tab draws, and the money tiles are the top and
 * bottom of the quote. Nothing on this tab is a second, shorter telling of the same facts written
 * separately; a summary that can disagree with its own detail is worse than no summary.
 */

export interface SummaryTabProps {
	request: CampaignRequestDetail;
	events: CampaignRequestEvent[];
	/** Jump to the Activity tab rather than repeating the whole timeline here. */
	onOpenActivity: () => void;
}

/** The earliest dial start and the latest dial end across the markets. */
function dialWindow(request: CampaignRequestDetail): string {
	const starts = request.marketSchedules.map((market) => market.dialStartDate).filter(Boolean) as string[];
	const ends = request.marketSchedules.map((market) => market.dialEndDate).filter(Boolean) as string[];

	if (starts.length === 0 && ends.length === 0) return "Not scheduled yet";

	return formatDateRange(starts.toSorted()[0] ?? null, ends.toSorted().at(-1) ?? null);
}

export function SummaryTab({ request, events, onOpenActivity }: SummaryTabProps): React.JSX.Element {
	const items = React.useMemo(() => buildTimeline(request, events), [request, events]);

	const now = items.findLast((item) => item.status === "now") ?? items.findLast((item) => item.status === "done");
	const next = items.find((item) => item.status === "ahead");

	const quote = request.currentQuote;
	const currency = quote?.currency ?? "SGD";
	const estimate = quoteTotalRange(quote);

	return (
		<Stack spacing={2}>
			{/*
			 * The canvas asks for four money tiles, one of which is a running "total to date". Three
			 * are drawn. Call time is not metered — nothing records what a call cost at the rate the
			 * quote froze — so the tile says that in three words instead of a figure that would look
			 * like a bill and reconcile with no invoice. And a "total to date" is, with call time
			 * unmetered, the set-up figure printed a second time under a different name.
			 */}
			{quote ? (
				<Grid container spacing={2}>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Tile
							label="Setup — Fixed"
							value={quote.confirmedAt ? formatMoney(quote.fixedTotal, currency) : "Not committed yet"}
							note={quote.confirmedAt ? "Firm. Billed at completion." : "Committed when you confirm the quote."}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Tile label="Call time so far" value={NOT_METERED} note="We do not publish a running figure yet." />
					</Grid>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Tile
							label="Estimated total"
							value={estimate ? formatMoneyRange(estimate.low, estimate.high, currency) : "—"}
							note="Setup plus estimated call time."
						/>
					</Grid>
				</Grid>
			) : null}

			<Panel
				title="Where it is"
				action={
					<Button size="small" variant="text" onClick={onOpenActivity}>
						Full Activity
					</Button>
				}
			>
				<Facts>
					<Field
						label="Now"
						value={
							now ? (
								<Box>
									{now.label}
									{now.detail ? (
										<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
											{now.detail}
										</Typography>
									) : null}
								</Box>
							) : (
								"—"
							)
						}
					/>
					<Field
						label="Next"
						value={next ? `${next.label}${next.time === "—" ? "" : ` · ${next.time}`}` : "Nothing after this"}
					/>
					<Field label="Calling" value={dialWindow(request)} />
				</Facts>
			</Panel>

			<Panel title="Outstanding">
				<BlockerChips blockers={request.blockers} explain emptyContent={<Muted>Nothing outstanding.</Muted>} />
			</Panel>

			<Panel title="The request">
				<Facts>
					<Field label="Contacts to call" value={request.expectedContacts?.toLocaleString("en-SG") ?? "—"} />
					<Field
						label="Markets"
						value={
							request.markets.length > 0
								? request.markets.map((market) => `${marketFlag(market)} ${marketLabel(market)}`).join(" · ")
								: "—"
						}
					/>
					{/*
					 * The client's own estimates, not the quote's. The Quote tab publishes the copies
					 * the price was frozen against, which is a different fact the moment either moves —
					 * that difference is the whole reason both are printed.
					 */}
					<Field label="Expected reach rate" value={formatPercent(request.expectedReachRate)} />
					<Field
						label="Average call length"
						value={request.expectedCallMinutes ? `${request.expectedCallMinutes} min` : "—"}
					/>
					<Field label="Lead list due" value={formatDate(request.leadsDueDate)} />
				</Facts>

				{request.notes ? (
					<Box>
						<Typography variant="caption" color="text.secondary">
							Your notes
						</Typography>
						<Typography variant="body2">{request.notes}</Typography>
					</Box>
				) : null}
			</Panel>
		</Stack>
	);
}
